import { BaseModule } from '@core/base/module/base-module'
import useFuzzy from '@/core/hooks/useFuzzy/useFuzzy'
import {
    FilterModuleConfig,
    FilterOptions,
    FilterState,
    TGenericFilterType,
    FilterConfig,
} from './types'

export interface IUnifiedFilterState {
    filter: FilterState
    search: string
}

export interface SearchKeyConfig {
    value: string
    threshold?: number
}

export interface UnifiedFilterConfig extends FilterModuleConfig {
    searchKeys?: SearchKeyConfig[]
    sortStrategy?: (a: any, b: any) => number
    dataTransformer?: (data: any[]) => any[]
    dynamicFilters?: {
        [filterType: string]: {
            accessor: string
            label: string
            collapsible?: boolean
            withSearch?: boolean
            extractor: (data: any[]) => string[]
        }
    }
}

export class UnifiedFilterModule extends BaseModule<IUnifiedFilterState> {
    private filterConfig: UnifiedFilterConfig

    constructor(config: UnifiedFilterConfig, moduleName: string) {
        const initialState: IUnifiedFilterState = {
            filter: { ...config.defaultFilters },
            search: '',
        }
        super(initialState, moduleName)
        this.filterConfig = config
    }

    setFilter(type: TGenericFilterType, activeFilters: string[]) {
        const currentFilter = this.store.get().filter
        const configSection = this.filterConfig.config[type]

        if (!configSection) {
            console.warn(`Filter type "${type}" not found in config`)
            return
        }

        const updatedSection: Record<string, boolean> = {}
        Object.keys(configSection.filters).forEach((filterKey) => {
            updatedSection[filterKey] = activeFilters.includes(filterKey)
        })

        this.store.set({
            ...this.store.get(),
            filter: {
                ...currentFilter,
                [type]: updatedSection,
            },
        })
    }

    getFilterOptionsAndState(type: TGenericFilterType): FilterOptions {
        const configSection = this.filterConfig.config[type]
        const currentFilters = this.store.get().filter[type] ?? {}

        if (!configSection) {
            console.warn(`Filter type "${type}" not found in config`)
            return {
                options: [],
                activeOptions: [],
                withSearch: false,
                collapsible: true,
            }
        }

        const filterOptions = Object.keys(configSection.filters).map((key) => {
            const filterConfig = configSection.filters[key]
            return {
                id: key,
                label: filterConfig.label,
                icon: filterConfig.icon,
                accessor: filterConfig.accessor,
            }
        })

        const activeOptions = Object.keys(currentFilters).reduce(
            (acc, currFilterKey) => {
                if (currentFilters[currFilterKey]) {
                    acc.push(currFilterKey)
                }
                return acc
            },
            [] as string[]
        )

        return {
            options: filterOptions,
            activeOptions,
            withSearch: configSection.withSearch,
            collapsible: configSection.collapsible,
        }
    }

    setSearchKey(value: string) {
        this.store.setKey('search', value)
    }

    getSearchKey(): string {
        return this.store.get().search
    }

    processData(data: any[], disableFilters = false): any[] {
        const transformedData = this.transformData(data)

        if (disableFilters) {
            return transformedData
        }

        const filteredData = this.applyFiltersToData(
            transformedData,
            this.store.get().filter
        )

        const searchFilteredData = this.applySearch(filteredData)

        const sortedData = this.applySorting(searchFilteredData)

        return sortedData
    }

    private applyFiltersToData(
        data: any[],
        filters: Record<string, Record<string, boolean>>
    ): any[] {
        return data.filter((item: any) => {
            return Object.entries(filters).every(
                ([filterGroupName, filterGroup]) => {
                    const selectedFilters = Object.keys(filterGroup).filter(
                        (filterName) => filterGroup[filterName]
                    )

                    if (selectedFilters.length === 0) {
                        return true
                    }

                    const searchKey = this.filterConfig.searchKeys?.find(
                        (key) => key.value.startsWith(filterGroupName + '.')
                    )

                    if (searchKey) {
                        const [parentKey, childKey] = searchKey.value.split('.')
                        const parentValue = item[parentKey]

                        if (Array.isArray(parentValue)) {
                            return selectedFilters.some((filter) =>
                                parentValue.some(
                                    (arrayItem) =>
                                        String(
                                            arrayItem[childKey]
                                        )?.toLowerCase() ===
                                        filter.toLowerCase()
                                )
                            )
                        }
                    }

                    const itemValue = item[filterGroupName]
                    return selectedFilters.some(
                        (filter) =>
                            String(itemValue)?.toLowerCase() ===
                            filter.toLowerCase()
                    )
                }
            )
        })
    }

    private transformData(data: any[]): any[] {
        if (!data || !Array.isArray(data)) {
            return []
        }
        if (this.filterConfig.dataTransformer) {
            return this.filterConfig.dataTransformer(data)
        }
        return data
    }

    private applySearch(data: any[]): any[] {
        const search = this.store.get().search.trim()
        if (!search) {
            return data
        }

        const searchKeys = this.filterConfig.searchKeys ?? []
        if (searchKeys.length === 0) {
            return data
        }

        const fuseKeys = searchKeys.map((keyConfig) => ({
            name: keyConfig.value,
            threshold: keyConfig.threshold || 0.5,
        }))

        const fuseOptions = {
            keys: fuseKeys,
        }
        const { results } = useFuzzy(data, search, fuseOptions)

        return results
    }

    private applySorting(data: any[]): any[] {
        if (this.filterConfig.sortStrategy) {
            return [...data].sort(this.filterConfig.sortStrategy)
        }
        return data
    }

    clearAllFilters() {
        const currentFilter = this.store.get().filter
        const clearedFilter = { ...currentFilter }

        Object.keys(clearedFilter).forEach((filterType) => {
            const filterSection = clearedFilter[filterType]
            if (filterSection && typeof filterSection === 'object') {
                Object.keys(filterSection).forEach((key) => {
                    filterSection[key] = false
                })
            }
        })

        this.store.setKey('filter', clearedFilter)
    }

    hasActiveFilters(): boolean {
        const { filter, search } = this.store.get()

        if (search && search.trim() !== '') {
            return true
        }

        return Object.values(filter).some((filterSection) =>
            Object.values(filterSection).some((value) => value === true)
        )
    }

    getFilterConfig() {
        return this.filterConfig.config
    }

    updateFilterConfig(newConfig: FilterConfig) {
        this.filterConfig.config = newConfig
    }

    initDynamicFilters(data: any[]) {
        if (!this.filterConfig.dynamicFilters) {
            return
        }

        const transformedData =
            this.filterConfig.dataTransformer?.(data) ?? data

        Object.entries(this.filterConfig.dynamicFilters).forEach(
            ([filterType, config]) => {
                const uniqueValues = config.extractor(transformedData)
                this.updateDynamicFilterOptions(filterType, uniqueValues)
            }
        )
    }

    updateDynamicFilterOptions(filterType: string, filterOptions: string[]) {
        const dynamicFilters = filterOptions.reduce(
            (acc, option) => {
                acc[option] = false
                return acc
            },
            {} as Record<string, boolean>
        )

        const currentFilter = this.store.get().filter
        this.store.set({
            ...this.store.get(),
            filter: {
                ...currentFilter,
                [filterType]: dynamicFilters,
            },
        })

        const currentConfig = this.getFilterConfig()
        const dynamicConfig = this.filterConfig.dynamicFilters?.[filterType]

        if (dynamicConfig) {
            const updatedConfig = {
                ...currentConfig,
                [filterType]: {
                    accessor: dynamicConfig.accessor,
                    label: dynamicConfig.label,
                    collapsible: dynamicConfig.collapsible,
                    withSearch: dynamicConfig.withSearch,
                    filters: filterOptions.reduce(
                        (acc, option) => {
                            acc[option] = {
                                id: option,
                                label: option,
                                accessor: dynamicConfig.accessor,
                            }
                            return acc
                        },
                        {} as Record<string, any>
                    ),
                },
            }

            this.updateFilterConfig(updatedConfig)
        }
    }

    getActiveFilterCounts() {
        const { filter } = this.store.get()
        const counts: Record<string, number> = {}

        Object.keys(filter).forEach((filterType) => {
            const filterSection = filter[filterType]
            counts[filterType] =
                Object.values(filterSection).filter(Boolean).length
        })

        return counts
    }

    getActiveFilters() {
        const { filter } = this.store.get()
        const activeFilters: Array<{
            filterName: string
            filters: Array<{ id: string; name: string }>
        }> = []

        Object.keys(filter).forEach((filterType) => {
            const filterConfig = this.filterConfig.config[filterType]
            const currentState = filter[filterType]

            if (currentState && typeof currentState === 'object') {
                const enabledOptions = Object.keys(currentState).filter(
                    (key) => (currentState as any)[key]
                )

                if (enabledOptions.length > 0) {
                    const filterItems = enabledOptions.map((optionId) => {
                        const option = filterConfig.filters[optionId]
                        return {
                            id: optionId,
                            name: option?.label ?? optionId,
                        }
                    })

                    activeFilters.push({
                        filterName: filterConfig.label,
                        filters: filterItems,
                    })
                }
            }
        })

        return activeFilters
    }
}
